import type { SamplingContext } from '@sentry/core';

import { getDealPackageRouteTraceSampleRate } from './deal-package-route-trace-sample-rate';
import { isDealPackageDocumentPathname } from './is-deal-package-document-pathname';

function pathnameFromSamplingContext(context: SamplingContext): string | null {
  const request = context.request;
  if (request?.url) {
    try {
      return new URL(request.url).pathname;
    } catch {
      // fall through
    }
  }

  const name = context.transactionContext?.name;
  if (typeof name === 'string') {
    const match = name.match(/\s(\/[^\s?#]+)/);
    if (match?.[1]) return match[1];
  }

  const rawPath =
    context.attributes?.['http.target'] ?? context.attributes?.['url.path'];
  if (typeof rawPath === 'string') return rawPath;

  return null;
}

function pageloadPathname(context: SamplingContext, op: string): string | null {
  const url =
    context.attributes?.['url.full'] ??
    context.attributes?.['http.url'] ??
    context.request?.url;
  if (typeof url === 'string') {
    try {
      return new URL(url).pathname;
    } catch {
      // fall through
    }
  }

  const name = context.name ?? context.transactionContext?.name;
  if (typeof name === 'string') {
    if (name.startsWith('/')) {
      return name.split(/[?#]/)[0] ?? name;
    }
    const fromName = pathnameFromSamplingContext(context);
    if (fromName) return fromName;
  }

  if (
    (op === 'pageload' || op === 'navigation') &&
    typeof globalThis.window !== 'undefined'
  ) {
    return window.location.pathname;
  }

  return null;
}

function isClientPageload(context: SamplingContext): boolean {
  const op =
    context.attributes?.['sentry.op'] ??
    context.transactionContext?.op ??
    context.attributes?.['operation.name'];
  if (op !== 'pageload' && op !== 'navigation') return false;

  const pathname = pageloadPathname(context, String(op));
  return pathname != null && isDealPackageDocumentPathname(pathname);
}

function isServerDocumentNavigation(context: SamplingContext): boolean {
  const pathname = pathnameFromSamplingContext(context);
  if (!pathname || !isDealPackageDocumentPathname(pathname)) return false;

  const method =
    context.request?.method ??
    context.attributes?.['http.request.method'] ??
    context.attributes?.['http.method'];
  if (method && String(method).toUpperCase() !== 'GET') return false;

  const headers = context.request?.headers;
  if (headers) {
    const rsc = headers.get?.('rsc') ?? headers.get?.('RSC');
    if (rsc === '1') return false;
    const dest = headers.get?.('sec-fetch-dest');
    if (dest && dest !== 'document') return false;
  }

  return true;
}

/**
 * Elevated sampling for deal-package document navigations via
 * {@link getDealPackageRouteTraceSampleRate}; all other transactions keep
 * `defaultSampleRate` (with parent inheritance on the fallback path).
 */
function dealPackageForcedSampleRate(defaultSampleRate: number): number {
  return getDealPackageRouteTraceSampleRate(defaultSampleRate);
}

/**
 * Deal-package document routes: force sampling on the browser root
 * (`pageload` / `navigation`) so SSR inherits a sampled parent. Server
 * document GETs honor an existing parent decision; forced rate applies only
 * when no parent sampling decision is present.
 */
export function createDealPackageRouteTracesSampler(defaultSampleRate: number) {
  return (context: SamplingContext): number => {
    if (isServerDocumentNavigation(context)) {
      if (typeof context.parentSampled === 'boolean') {
        return Number(context.parentSampled);
      }
      return dealPackageForcedSampleRate(defaultSampleRate);
    }
    if (isClientPageload(context)) {
      return dealPackageForcedSampleRate(defaultSampleRate);
    }
    return context.inheritOrSampleWith
      ? context.inheritOrSampleWith(defaultSampleRate)
      : typeof context.parentSampled === 'boolean'
        ? Number(context.parentSampled)
        : defaultSampleRate;
  };
}
